import { Box, Button, Flex, Img, Text, useMediaQuery, type FlexProps } from "@chakra-ui/react"
import linkedInLogo from "../../assets/images/LIlogo.svg"
import { Paths, headerFooterContentWidth, headerFooterMaxWidth, mobileBreakpoint } from "../../common/commonConstants"
import { footerContainer, hStackOverlay } from "./footer.style-overrides"
import { ContactUs } from "./footer.sub.contactus"

import { chakraTheme } from "../../theme/chakraTheme"
import { getLandscapeOrPortraitValues } from "../commons/utils"
import { SnapFooterIcon } from "../images"

const containerStyles: FlexProps = {
  w: "100%",
  h: "100%",
  justify: "flex-end",
  flexDir: "column",
  align: "center",
  mt: 0
}

export const Footer = (): JSX.Element => {
  const [isDesktop] = useMediaQuery(`(min-width: ${mobileBreakpoint}px)`)
  const navigateToTermsAndConditions = (): void => {
    window.open(Paths.TERMSANDCONDITIONS, "_blank", "rel=noopener noreferrer")
  }
  const navigateToPrivacyPolicy = (): void => {
    window.open(Paths.PRIVACYPOLICY, "_blank", "rel=noopener noreferrer")
  }

  const isLandscape = window.matchMedia("(orientation: landscape)").matches

  return (
    <Box
      {...footerContainer}
      py={"16px"}
      px={[0, 0, getLandscapeOrPortraitValues("2", "0", isLandscape), "44px", "44px", "44px"]}
      w={["100%"]}
      pb={"env(safe-area-inset-bottom)"}
      data-name="footer-container"
    >
      <Box
        maxW={headerFooterContentWidth}
        w="100%"
        style={{ maxWidth: headerFooterMaxWidth }}
        margin="auto"
        display={["block", "block", "grid", "grid"]}
        gridTemplateColumns={["1fr", "1fr", "1fr 1fr 1fr", "1fr 1fr 1fr"]}
        gap={["0", "0", "24px", "24px"]}
        data-name="footer-grid-container"
      >
        <Flex
          {...hStackOverlay}
          flexWrap={["wrap", "wrap", "nowrap", "nowrap"]}
          justify={["center", "center", "space-between", "space-between"]}
          textAlign={["center", "initial", "initial"]}
          align={"flex-end"}
          data-name="footer-main-flex"
          display={["flex", "flex", "contents", "contents"]}
        >
          <Img
            display={["block", "block", "none", "none"]}
            cursor="pointer"
            opacity={1}
            src={SnapFooterIcon}
            maxW="140px"
            alt="Snap Fire Systems Logo"
            data-name="footer-logo-mobile"
          />

          <Flex {...containerStyles} align={["center", "center", "flex-start"]} data-name="footer-contact-container">
            <Box data-name="contact-us-wrapper">
              <ContactUs />
            </Box>
            {isDesktop && (
              <Flex
                align={["center", "center", "flex-end"]}
                justify={["center", "center", "flex-start"]}
                w="100%"
                h="35px"
                mt="18px"
                data-name="footer-links-desktop"
              >
                <Box display={"flex"} alignItems={"center"} data-name="footer-links-box-desktop">
                  <Button
                    variant="Button1Small"
                    fontFamily={"PT Sans"}
                    pl="0"
                    pr="4px"
                    py="0"
                    h="auto"
                    onClick={navigateToTermsAndConditions}
                    data-name="terms-conditions-button-desktop"
                  >
                    Terms & Conditions
                  </Button>
                  &nbsp;|&nbsp;
                  <Button
                    pl="2px"
                    fontFamily={"PT Sans"}
                    variant="Button1Small"
                    h="auto"
                    onClick={navigateToPrivacyPolicy}
                    data-name="privacy-policy-button-desktop"
                  >
                    Privacy Policy
                  </Button>
                </Box>
              </Flex>
            )}
          </Flex>
          <Flex {...containerStyles} data-name="footer-logo-section" justify={["flex-end", "flex-end", "space-between", "space-between"]}>
            <Img
              display={["none", "none", "block", "block"]}
              cursor="pointer"
              opacity={1}
              src={SnapFooterIcon}
              maxW="224px"
              position={"relative"}
              top="0px"
              data-name="footer-logo-desktop"
            />

            <Box mt={["16px", "18px", "18px"]} w="208px" display={["block", "block", "none", "none"]}>
              <Box mt="0">
                <Box mt="12px" fontWeight="700" fontSize="14px" lineHeight="17px">
                  <Flex alignItems={"center"} justify={["center", "center", "unset"]}>
                    <Img w="25px" mr="0" src={linkedInLogo} alt="LinkedIn" />
                    <a
                      href="https://www.linkedin.com/company/snap-fire-systems/mycompany/"
                      target="_blank"
                      rel="noreferrer"
                      style={{ position: "relative", ...(!isDesktop && { fontSize: "12px", paddingLeft: "8px", left: "3px" }) }}
                      {...chakraTheme?.components?.Button?.Button2Bold}
                    >
                      Connect with us
                    </a>
                  </Flex>
                </Box>
              </Box>
              <Flex
                align={["center", "center", "flex-end"]}
                justify={["center", "center", "flex-start"]}
                w={"100%"}
                h="35px"
                mt={["14px", "15px", "15px"]}
              >
                <Box display={"flex"} alignItems={"center"} mt="2">
                  <Button
                    variant={["RCaptionBold", "RCaptionBold", "Button1Small"]}
                    pl="0"
                    pt="4px"
                    py="0"
                    h="auto"
                    pr="8px"
                    onClick={navigateToTermsAndConditions}
                  >
                    Terms & Conditions
                  </Button>
                  <span style={{ fontSize: "12px" }}>|</span>
                  <Button pr="0" pl="8px" variant={["RCaptionBold", "RCaptionBold", "Button1Small"]} h="auto" onClick={navigateToPrivacyPolicy}>
                    Privacy Policy
                  </Button>
                </Box>
              </Flex>
            </Box>

            <Flex align={["center", "center", "flex-end"]} h="35px" mt={["2px", 0, "12px"]}>
              <Text align="center" variant="caption" lineHeight={"19px"}>
                Snap Fire Systems Pty Ltd. <br /> ABN 73 120 808 326 | Accreditation - ISO: 9001
              </Text>
            </Flex>
          </Flex>
          <Flex {...containerStyles} alignItems={["center", "center", "flex-start", "flex-end", "flex-end", "flex-end"]} justifyContent={"space-between"} data-name="linked-container">
            <Box mt="26px" display={["none", "none", "block", "block"]}>
              <Box mt="17px" fontWeight="700" fontSize="14px" lineHeight="17px" pl="6px">
                <Flex alignItems={"center"} justify={["center", "center", "unset"]}>
                  <Img w="25px" mr="2" src={linkedInLogo} alt="LinkedIn" />
                  <a
                    href="https://www.linkedin.com/company/snap-fire-systems/mycompany/"
                    target="_blank"
                    rel="noreferrer"
                    {...chakraTheme?.components?.Button?.Button2Bold}
                  >
                    Connect with us
                  </a>
                </Flex>
              </Box>
            </Box>

            <Box h={["auto", "auto", "44px"]} display={"flex"} alignItems={"flex-end"} mt={["1", "1", "2px"]}>
              <Text variant="caption">{new Date().getFullYear()} © IG6 Pty Ltd. All Rights Reserved.</Text>
            </Box>
          </Flex>
        </Flex>
      </Box>
    </Box>
  )
}
